import { CellDoubleClickedEvent, ColDef, Column, FilterModel, GridApi, IRowNode, RowNode, SetFilterModel, SortState } from '@ag-grid-community/core';
import type { CellDataTypes, ChangeState, ColumnLayoutSchema, DataTaskRequest, DropdownConfig, GridColumn, GridColumnSetting, GridDataTask, GridFilterSetting, GridFilterType, GridRow, GridSortSetting, GridUserSettings, LookupConfig, TTGridAggregate, TTGridState } from '../grid.types';

/**
 * Checks whether the given value matches the interface of a `DataTaskRequest`.
 *
 * @param value the value to check if is a data task request.
 * @returns `true` if the value matches the interface of a `DataTaskRequest`, `false` if not.
 */
export function isDataTaskRequest(value: unknown): value is DataTaskRequest {
    if (!!value && typeof value === 'object' && Object.hasOwn(value, 'method') && Object.hasOwn(value, 'parameters')) {
        return true;
    }
    return false;
}

/**
 * Checks whether the given column-editable property of a gridcolumn is of type LookupConfig.
 *
 * @param editable the editable property of a column to check if is a LookupConfig.
 * @returns true if the editable is a LookupConfig, false if not.
 */
export function isEditableLookup(editable: unknown): editable is LookupConfig {
    if (editable && typeof editable === 'object' && Object.hasOwn(editable, 'lookup') && (editable as LookupConfig).lookup !== null && !isNaN(Number((editable as LookupConfig).lookup))) {
        return true;
    }
    return false;
}

export function isEditableDropdown(editable: unknown): editable is DropdownConfig {
    return isEditableLookup(editable) && editable.type === 'dropdown';
}

export function isEditableMultiLookup(editable: unknown): editable is DropdownConfig {
    return isEditableLookup(editable) && editable.type === 'multi';
    // return true;
}

/**
 * Formats the number with seperators.
 *
 * @param number the number to format.
 * @param decimals how many decimals to format the number with.
 * @returns a formatted string representation of the number.
 */
export function formatNumber(number: number | string, decimals?: number | null): string {
    const formatter = new Intl.NumberFormat('nb-NO', { maximumFractionDigits: decimals || undefined, minimumFractionDigits: decimals || undefined });

    if (typeof number === 'string') number = parseFloat(number);

    if (!isNaN(number)) {
        return formatter.format(number);
    }

    return number.toFixed(decimals || 0).replace('.', ',');
}

/**
 * Calculates the aggregate for the given column using the given aggregate function.
 *
 * @param aggregateFunction the aggregate function to calculate with.
 * @param colDef the field to calculate the aggregate for.
 * @returns a string or number containing the aggregation value.
 */
export function getAggregateValue<TType extends GridRow = GridRow>(aggregate: TTGridAggregate, colDef: ColDef, rows: TType[], footerRowData: GridRow & { _id: 'aggregaterow' }): number | string {
    let value: number | string = '';

    switch (aggregate.aggregate) {
        case 'count':
            value = 'Total Count: ' + rows.filter((row) => !!row[colDef.field!]).length;
            break;
        case 'sum':
            value = rows.reduce((sum, row) => sum + (Number(row[colDef.field!]) || 0), 0);
            break;
        case 'average':
            value = 'Average: ' + formatNumber(rows.reduce((sum, row) => sum + (Number(row[colDef.field!]) || 0), 0) / rows.filter((row) => !!row[colDef.field!]).length, 2);
            break;
        case 'min':
        case 'max':
            value = 'Min: ' + Math.min(...rows.map((row) => Number(row[colDef.field!]))) + ' Max: ' + Math.max(...rows.map((row) => Number(row[colDef.field!])));
            break;
        case 'custom':
            if (aggregate.aggregate_function) {
                let aggregateFormula = aggregate.aggregate_function;

                for (let [key, value] of Object.entries(footerRowData)) {
                    aggregateFormula = aggregateFormula.replaceAll(key, value);
                }

                try {
                    value = Function('return ' + aggregateFormula)();
                } catch (error) {
                    value = '';
                }
            }
            break;
        default:
            console.error('Invalid aggregate function >>: ', colDef);
            value = '';
    }

    return value;
}

/**
 * Checks if the given row node is the row created for displaying aggregations without row grouping.
 *
 * @param node the node to check if is the aggregation row.
 * @returns `true` if the given row node is the aggregation row, `false` if not.
 */
export function isAggregationRow(node: IRowNode): boolean {
    return node.data?.['_id'] === 'aggregaterow';
}

export function isDateTimeColumn(column: GridColumn) {
    return getCellDataType(column) === 'datetime';
}

export function isDateColumn(column: GridColumn) {
    return getCellDataType(column) === 'date';
}

export function isNumberColumn(column: GridColumn) {
    return getCellDataType(column) === 'number';
}

/**
 * Checks whether the given column is a checkbox column.
 *
 * @param column the column to check if is a checkbox column.
 * @returns `true` if the given column is a checkbox column, `false` if not.
 */
export function isCheckboxColumn(column: GridColumn): boolean {
    return getCellDataType(column) === 'boolean';
}

export function isLongTextColumn(column: GridColumn): boolean {
    return column.coltype === 'long varchar' || column.coltype === 'long nvarchar';
}

/**
 * Returns the cell data type based on the given column schema.
 *
 * @param column the column schema to get cell data for.
 * @returns the cell data type for the given column schema.
 */
export function getCellDataType(column: GridColumn): CellDataTypes {
    if (isChipsColumn(column)) return 'object';

    switch (column.coltype) {
        case 'currency':
            return 'currency';
        case 'decimal':
        case 'integer':
        case 'number':
        case 'numeric':
        case 'tinyint':
        case 'smallint':
        case 'unsigned int':
            return 'number';
        case 'date':
            return 'date';
        case 'datetime':
        case 'timestamp':
            return 'datetime';
        case 'time':
            return 'time';
        case 'bit':
        case 'bool':
        case 'boolean':
            return 'boolean';
        default:
            return 'text';
    }
}

export function isChipsColumn(column: GridColumn) {
    return column.colname.endsWith('_chips') || isEditableMultiLookup(column.editable);
}

export function isAggregateConfigDefined(config: boolean | TTGridAggregate[] | null | undefined): config is boolean | TTGridAggregate[] {
    return config !== undefined && config !== null && (typeof config === 'boolean' || (!!config && config instanceof Array && config.length > 0));
}

/**
 * Maps the given grid-usersettings object to a grid-state object used for setting the initial state of ag-grid.
 *
 * @param settings the grid-usersettings object to map to an ag-grid initial grid-state object.
 * @param columns the column-definitions of the grid for which to map the settings object to. Required for mapping filters, if not provided all filters will be empty.
 * @returns an ag-grid grid-state object.
 */
export function userSettingsToGridState(settings: GridUserSettings | undefined, columns?: ColDef[]): TTGridState {
    let state: TTGridState = { floatingFilter: !!settings?.filterable, agGrid: true, wrapHeaderText: false, wrapText: false };

    function addHiddenColumn(colId: string) {
        if (!state.columnVisibility) {
            state.columnVisibility = { hiddenColIds: [] };
        }

        state.columnVisibility.hiddenColIds.push(colId);
    }

    function addColumnWidth(colId: string, width: number) {
        if (!state.columnSizing) {
            state.columnSizing = { columnSizingModel: [] };
        }

        state.columnSizing.columnSizingModel.push({ colId: colId, width: width });
    }

    function addColumnPinned(colId: string, pinned: 'left' | 'right' | boolean) {
        if (!state.columnPinning) {
            state.columnPinning = { rightColIds: [], leftColIds: [] };
        }

        if (pinned === 'left') {
            state.columnPinning.leftColIds.push(colId);
        } else if (pinned === 'right' || pinned === true) {
            state.columnPinning.rightColIds.push(colId);
        }
    }

    for (let [key, colSetting] of Object.entries(settings?.columns ?? {})) {
        if (colSetting.hidden) addHiddenColumn(key);
        if (colSetting.width) addColumnWidth(key, colSetting.width);
        if (colSetting.pinned) addColumnPinned(key, colSetting.pinned);
    }

    if (settings?.page || settings?.pageSize) {
        if (!state.pagination) {
            state.pagination = { page: (settings.page ?? 1) - 1, pageSize: settings.pageSize };
        }
    }

    if (settings?.sort) {
        if (!state.sort) {
            state.sort = { sortModel: [] };
        }

        state.sort.sortModel = settings.sort.sort((a, b) => (a.index || 0) - (b.index || 0)).map((sortSetting) => ({ sort: sortSetting.dir, colId: sortSetting.field }));
    }

    if (settings?.filter?.filters && !!columns) {
        if (!state.filter) {
            state.filter = { filterModel: {} };
        }

        for (let filter of settings?.filter?.filters ?? []) {
            let column = columns.find((col) => col.colId === filter.field);
            if (column) {
                state.filter.filterModel![filter.field] = getFilerModelFromGridFilterSetting(filter, column);
            }
        }
    }

    if (Object.values(settings?.columns ?? {}).some((column) => column.order !== undefined)) {
        if (!state.columnOrder) {
            state.columnOrder = { orderedColIds: [] };
        }

        state.columnOrder.orderedColIds = Object.entries(settings?.columns ?? {})
            .sort(([_a, a], [_b, b]) => {
                if (a.order === undefined || a.order === null) return 1;
                if (b.order === undefined || b.order === null) return 1;
                return (a.order ?? -1) - (b.order ?? -1);
            })
            .map(([key, _]) => key);
    }

    return state;
}

/**
 * Retrieves the filtermodel
 */
function getFilerModelFromGridFilterSetting(filter: GridFilterSetting, column: ColDef) {
    let filterModel: any = {};
    try {
        if (column.cellDataType === 'boolean') {
            filterModel = getTextFilterModel(filter);
        } else {
            filterModel = {
                filterType: 'multi',
                filterModels: [],
            };

            switch (column.cellDataType) {
                case 'date':
                case 'datetime':
                    filterModel.filterModels.push(getDateFilterModel(filter));
                    break;
                default:
                    filterModel.filterModels.push(getTextFilterModel(filter));
                    break;
            }

            if (!!filter.set && filter.set.length > 0) {
                filterModel.filterModels.push(getSetFilterModel(filter));
            } else {
                filterModel.filterModels.push(null);
            }
        }
    } catch (error) {
        console.log(error);
    }

    return filterModel;
}

/**
 * Creates and returns the filter model for a date filter based on the provided filter setting.
 *
 * @param filter the filter setting to create a date filter model of.
 * @returns a date filter model based on the given filter setting.
 */
function getDateFilterModel(filter: GridFilterSetting): FilterModel {
    const timezoneOffset = new Date().getTimezoneOffset() * 60000;

    return {
        dateFrom: filter.value ? new Date((new Date(getFilterValueFromGridFilterSetting(filter)) as any) - timezoneOffset).toISOString().substring(0, 10).replace('T', ' ') : null,
        dateTo: null,
        filterType: 'date',
        type: getFilterType(filter.operator) ?? '',
    };
}

/**
 * Creates and return the text filter model based on the provided filter setting.
 *
 * @param filter the filter setting to create a text filter model of.
 * @returns a text filter model based on the provided filter setting.
 */
function getTextFilterModel(filter: GridFilterSetting): FilterModel {
    return {
        type: getFilterType(filter.operator) ?? '',
        filterType: 'text',
        filter: getFilterValueFromGridFilterSetting(filter),
    };
}

/**
 * Retrieves the filter value of the given filter setting.
 *
 * @param filter the filter setting to retrieve the filter value of.
 * @returns the filter value of the given filter setting.
 */
function getFilterValueFromGridFilterSetting(filter: GridFilterSetting) {
    return filter.base64Encoded !== true ? filter.value : !!filter.value ? atou(filter.value) : '';
}

/**
 * Creates and return a set filter model based of the provided filter setting.
 *
 * @param filter the filter setting to create a set filter model of.
 * @returns a set filter model based on hte provided filter setting.
 */
function getSetFilterModel(filter: GridFilterSetting): SetFilterModel {
    return {
        filterType: 'set',
        values: filter.set ?? [],
    };
}

export function gridStateToColumnLayout(state: TTGridState): ColumnLayoutSchema {
    let schema: ColumnLayoutSchema = {};

    function addPropertyToColumnSetting(colId: string, setting: Partial<GridColumnSetting>) {
        if (colId === 'ag-Grid-AutoColumn') return;

        if (!schema[colId]) {
            schema[colId] = {};
        }
        schema[colId] = { hidden: false, filter: null, width: null, ...schema[colId], ...setting };
    }

    state.columnVisibility?.hiddenColIds.forEach((colId) => addPropertyToColumnSetting(colId, { hidden: true }));
    state.columnSizing?.columnSizingModel.forEach((colState) => addPropertyToColumnSetting(colState.colId, { width: colState.width }));
    state.columnOrder?.orderedColIds.forEach((colId, index) => addPropertyToColumnSetting(colId, { order: index }));

    if (state.filter?.filterModel) {
        schema.tt_grid_filterable = true;
        const filters = getFilterModelAsGridFilterSettings(state.filter.filterModel, false, true).filters;

        for (let filter of filters) {
            addPropertyToColumnSetting(filter.field, { filter: filter });
        }
    }

    return schema;
}

/**
 * Converts the given grid state to a grid user settings object.
 *
 * @returns the grid user settings object.
 */
export function gridStateToUserSettings(state: TTGridState, serverSide?: boolean | undefined, decode: boolean = false): GridUserSettings {
    let settings: GridUserSettings = {
        columns: {},
        filter: {
            filters: [],
            base64Encoded: undefined,
        },
        sort: [],
        filterable: state.floatingFilter === true ? { mode: 'row' } : false,
    };

    function addPropertyToColumnSetting(colId: string, setting: Partial<GridColumnSetting>) {
        if (!settings.columns[colId]) {
            settings.columns[colId] = {};
        }
        settings.columns[colId] = { ...settings.columns[colId], ...setting };
    }

    state.columnVisibility?.hiddenColIds.forEach((colId) => addPropertyToColumnSetting(colId, { hidden: true }));
    state.columnSizing?.columnSizingModel.forEach((colState) => addPropertyToColumnSetting(colState.colId, { width: colState.width }));
    state.columnOrder?.orderedColIds.forEach((colId, index) => addPropertyToColumnSetting(colId, { order: index }));
    state.columnPinning?.leftColIds.forEach((colId) => addPropertyToColumnSetting(colId, { pinned: 'left' }));
    state.columnPinning?.rightColIds.forEach((colId) => addPropertyToColumnSetting(colId, { pinned: 'right' }));

    if (state.sort?.sortModel) {
        settings.sort = getSortStateAsGridSortSettings(state.sort);
    }

    if (state.filter?.filterModel) {
        settings.filterable = true;
        settings.filter = getFilterModelAsGridFilterSettings(state.filter.filterModel, serverSide, decode);
    }

    if (state.pagination) {
        settings.page = state.pagination.page;
        settings.pageSize = state.pagination.pageSize;
    }

    return settings;
}

/**
 * Maps the given sort state to grid sort settings, used for user-settings for kendo grid and for serverside parameters.
 *
 * @param sortState the sort state to map into grid sort settings.
 * @returns a list containing the grid sort settings.
 */
export function getSortStateAsGridSortSettings(sortState?: SortState): GridSortSetting[] {
    return sortState?.sortModel?.map((sort, index) => ({ field: sort.colId, dir: sort.sort, index: index })) || [];
}

/**
 * Maps the given filterModel to a grid filter settings.
 *
 * @param filterModel the filter model to map to a grid filter setting.
 * @param serverSide whether the filter model is used for a serverside grid or not, default is ` false`.
 * @returns the filter model mapped to grid filter settings.
 */
export function getFilterModelAsGridFilterSettings(filterModel?: FilterModel, serverSide = false, decode: boolean = false): { filters: GridFilterSetting[] } {
    if (!filterModel) return { filters: [] };
    let settings: { filters: GridFilterSetting[] } = { filters: [] };

    for (let key in filterModel) {
        try {
            let operator;
            let value;
            let set: string[] = [];

            if (filterModel[key].filterType === 'multi') {
                const filterModels = filterModel[key].filterModels;

                if (filterModels[0]?.type) {
                    operator = getFilterOperatorType(filterModels[0].type);
                    value = getFilterValue(filterModels[0]);
                }

                if (filterModels[1]?.filterType === 'set') {
                    set = filterModels[1]?.values ?? [];
                }
            } else {
                operator = getFilterOperatorType(filterModel[key].type);
                value = getFilterValue(filterModel[key]);
            }

            let filter: GridFilterSetting;

            if (serverSide !== true) {
                if (decode) {
                    let decodedValue = value;

                    try {
                        decodedValue = atou(value);
                    } catch (_) {
                        decodedValue = value;
                    }

                    filter = { field: key, operator: operator as GridFilterType, value: decodedValue };
                } else {
                    filter = { field: key, operator: operator as GridFilterType, base64Encoded: true, value: !!value ? utoa(value) : value, set: [] };
                }
            } else {
                filter = { field: key, operator: operator as GridFilterType, value: value, set: [] };
            }
            settings.filters.push(filter);
        } catch (error) {
            console.log(error);
        }
    }

    return settings;
}

export function getRowNodesFromChildrenMapped(node: any) {
    if (!node) return [];

    if (Array.isArray(node)) {
        return node.slice(); // shallow copy
    }

    if (typeof node === 'object') {
        let out: any[] = [];

        if (node instanceof RowNode) {
            if (node.childrenMapped && Object.keys(node.childrenMapped).length === 0) {
                return [node];
            } else {
                out = out.concat(getRowNodesFromChildrenMapped(node.childrenMapped));
            }
        } else {
            for (const k of Object.keys(node)) {
                out = out.concat(getRowNodesFromChildrenMapped(node[k]));
            }
        }

        return out;
    }

    return [];
}

export function getChildrenMappedFromPivotKey(node: RowNode | { [key: string]: any }[] | { [key: string]: any }, pivotKey: string) {
    let nodes: any[] = [];

    if (!(node instanceof RowNode) && !Array.isArray(node) && pivotKey in node) {
        return node[pivotKey];
    } else if (!(node instanceof RowNode) && Array.isArray(node)) {
        nodes = node.map((n: any) => getChildrenMappedFromPivotKey(n, pivotKey));
    } else if (!!node.childrenMapped && pivotKey in node.childrenMapped) {
        return node.childrenMapped[pivotKey];
    } else if (!!node.childrenMapped) {
        nodes = Object.keys(node.childrenMapped).map((k) => getChildrenMappedFromPivotKey(node.childrenMapped![k], pivotKey));
    }

    return nodes.flat(50);
}

export function getDetailRowNodesFromPivotKeys(node: RowNode, pivotKeys: string[]): RowNode[] {
    let rootNode: RowNode | RowNode[] = Object.assign({}, node);
    let detailNodes = rootNode.childrenMapped;
    let usedKeys = false;

    for (let key of pivotKeys) {
        if (!!detailNodes && key in detailNodes) {
            usedKeys = true;
            detailNodes = detailNodes[key];
        } else {
            usedKeys = false;
        }
    }

    if (!usedKeys && pivotKeys.length > 0) {
        for (let key of pivotKeys) {
            rootNode = getChildrenMappedFromPivotKey(<RowNode>rootNode!, key);
        }

        detailNodes = Array.isArray(rootNode) ? rootNode.map((i: any) => getRowNodesFromChildrenMapped(i)).flat(50) : rootNode;
    }

    detailNodes = getRowNodesFromChildrenMapped(detailNodes);
    detailNodes = getRowNodesFromChildrenMapped(detailNodes);

    detailNodes = [detailNodes].flat(50);
    return detailNodes as RowNode[];
}

/**
 * Maps the given the operator filter type to a filter type used in ag-grid,
 *
 * @param operator the operator filter type to map to an ag-grid filter type.
 * @returns the ag-grid filter type.
 */
export function getFilterType(operator: GridFilterType): string {
    if (operator === 'contains') {
        return 'contains';
    } else if (operator === 'doesnotcontain') {
        return 'notContains';
    } else if (operator === 'eq') {
        return 'equals';
    } else if (operator === 'neq') {
        return 'notEqual';
    } else if (operator === 'startswith') {
        return 'startsWith';
    } else if (operator === 'endswith') {
        return 'endsWith';
    } else if (operator === 'gt') {
        return 'greaterThan';
    } else if (operator === 'gte') {
        return 'greaterThanOrEqual';
    } else if (operator === 'lt') {
        return 'lessThan';
    } else if (operator === 'lte') {
        return 'lessThanOrEqual';
    } else if (operator === 'isempty' || operator === 'isnull' || operator === 'isnullorempty') {
        return 'blank';
    } else if (operator === 'isnotempty' || operator === 'isnotnull' || operator === 'isnotnullorempty') {
        return 'notBlank';
    }

    return operator;
}

/**
 * Maps the given ag-grid filter type to a filter-operator type used in grid usersettings.
 *
 * @param filterType the ag-grid filter type to map to an operator type used in grid-usersettings.
 * @returns the operator type.
 */
export function getFilterOperatorType(filterType: string): GridFilterType {
    if (filterType === 'contains') {
        return 'contains';
    } else if (filterType === 'notContains') {
        return 'doesnotcontain';
    } else if (filterType === 'equals') {
        return 'eq';
    } else if (filterType === 'notEqual') {
        return 'neq';
    } else if (filterType === 'startsWith') {
        return 'startswith';
    } else if (filterType === 'endsWith') {
        return 'endswith';
    } else if (filterType === 'greaterThan') {
        return 'gt';
    } else if (filterType === 'greaterThanOrEqual') {
        return 'gte';
    } else if (filterType === 'lessThan') {
        return 'lt';
    } else if (filterType === 'lessThanOrEqual') {
        return 'lte';
    } else if (filterType === 'blank') {
        return 'isnullorempty';
    } else if (filterType === 'notBlank') {
        return 'isnotnullorempty';
    }

    throw Error('filter_not_supported');
}

/**
 * Gets the filter value from the given filter model.
 *
 * @param filterModel the filter model to get the filter value from.
 * @returns the filter value from the given filter model.
 */
export function getFilterValue(filterModel: FilterModel): any {
    if (filterModel['filterType'] === 'date') {
        return new Date(new Date(filterModel['dateFrom']).getTime() - new Date().getTimezoneOffset() * 60000);
    } else if (filterModel['filterType'] === 'multi') {
        return filterModel['filterModels'][0]['filter'];
    }

    return filterModel['filter'];
}

/**
 * Base64 encodes the given string.
 *
 * @param data the string to encode.
 * @returns the encoded the string.
 */
export function utoa(data: string): string {
    return btoa(unescape(encodeURIComponent(data)));
}

/**
 * Base64 decodes the diven string.
 *
 * @returns the base64 decoded string.
 */
export function atou(data: string): string {
    return decodeURIComponent(escape(atob(data)));
}

/**
 * If the given filtermodel has a text filter value, this value becomes encoded. If there is nothing to encode, the given filtermodel is simply returned.
 *
 * @param filterModel the filtermodel to encode.
 * @returns the encoded filtermodel if it has a text filter to encode.
 */
export function getEncodedFilterModel(filterModel?: FilterModel, serverSide: boolean = false) {
    if (serverSide !== true && !!filterModel) {
        console.log(filterModel);
        for (let key of Object.keys(filterModel)) {
            if (filterModel[key].filterType === 'multi') {
                let filter = filterModel[key].filterModels[0];

                if (!!filter && Object.hasOwn(filter, 'filter')) {
                    filterModel[key].filterModels[0] = { ...filter, filter: !!filter.filter ? utoa(filter.filter) : filter.filter, encoded: !!filter.filter };
                }

                let setFilter = filterModel[key].filterModels[1];

                if (!!setFilter && typeof setFilter === 'object' && 'filterType' in setFilter && setFilter.filterType === 'set' && 'values' in setFilter && Array.isArray(setFilter.values) && setFilter.values.every((item: unknown) => typeof item === 'string')) {
                    filterModel[key].filterModels[1] = { ...setFilter, values: setFilter.values.map((value: string) => (!!value ? utoa(value) : value)), encoded: true };
                }
            }
        }
    }

    return filterModel;
}

/**
 * If the given filtermodel has a text filter value which has been decoded, the value is decoded and set back to the filtermodel which is then returned.
 *
 * @param filterModel the filtermodel to decode.
 * @returns the decoded filtermodel if it has a text filter to encode.
 */
export function getDecodedFilterModel(filterModel?: FilterModel, serverSide: boolean = false) {
    if (serverSide !== true && !!filterModel) {
        for (let key of Object.keys(filterModel)) {
            if (filterModel[key].filterType === 'multi') {
                let filter = filterModel[key].filterModels[0];

                if (!!filter && Object.hasOwn(filter, 'filter')) {
                    filterModel[key].filterModels[0] = { ...filter, filter: !!filter.filter && filter.encoded === true ? atou(filter.filter) : filter.filter, encoded: undefined };
                }

                let setFilter = filterModel[key].filterModels[1];

                if (!!setFilter && typeof setFilter === 'object' && 'filterType' in setFilter && setFilter.filterType === 'set' && 'values' in setFilter && Array.isArray(setFilter.values) && setFilter.values.every((item: unknown) => typeof item === 'string')) {
                    filterModel[key].filterModels[1] = { ...setFilter, values: setFilter.values.map((value: string) => (!!value && setFilter.encoded === true ? atou(value) : value)), encoded: undefined };
                }
            }
        }
    }

    return filterModel;
}

function collectLeafDescendants(start: IRowNode): IRowNode[] {
    const out: IRowNode[] = [];
    const stack: IRowNode[] = [start];

    while (stack.length) {
        const node = stack.pop()!;
        if (!node) continue;

        if (!node.group) {
            out.push(node); // leaf
            continue;
        }

        // childrenMapped exists on RowNode (not IRowNode) -> cast.
        const mapped = (<RowNode>node).childrenMapped as Record<string, IRowNode> | undefined | null;

        if (mapped) {
            for (const child of Object.values(mapped)) {
                if (child) stack.push(child);
            }
            continue;
        }

        // Fallbacks if childrenMapped isn't present
        if (node.childrenAfterFilter) {
            for (const child of node.childrenAfterFilter) stack.push(child);
        } else if (node.childrenAfterGroup) {
            for (const child of node.childrenAfterGroup) stack.push(child);
        }
    }

    return out;
}

/**
 * Build an ordered list of row-group fields and a map of the clicked group path -> value.
 * Walks up the rowNode chain to the root, pairing keys to row-group columns.
 */
function getRowGroupContext(rowNode: IRowNode): Record<string, any> {
    const ctx: Record<string, any> = {};
    let curr: IRowNode | null = rowNode;

    while (curr && curr.level != null && curr.level >= 0) {
        if (curr.group) {
            // Prefer node.field (AG Grid exposes this on group nodes), else derive from rowGroupColumn.
            const field = (curr as any).field ?? curr.rowGroupColumn?.getColDef().field ?? undefined;
            if (field != null) ctx[field] = curr.key;
        }
        curr = curr.parent!;
    }
    return ctx;
}

/** Read pivot context without ColumnApi: use api.getPivotColumns() + colDef.pivotKeys. */
function getPivotContext(
    api: GridApi,
    colDef: ColDef,
): {
    pivotFields: string[];
    pivotValues: any[];
} {
    const pivotFields = (api.getPivotColumns?.() as Column[] | undefined)?.map((c) => c.getColDef().field!).filter(Boolean) ?? [];
    const pivotValues: any[] = (colDef as any).pivotKeys ?? [];
    return { pivotFields, pivotValues };
}

/**
 * Main utility: returns the leaf RowNodes that correspond to the double-clicked pivot cell.
 * Pass `returnData = true` to get the underlying row data array instead of RowNodes.
 */
export function getDrilldownForPivotCell(api: GridApi, rowNode: IRowNode, colDef: ColDef, returnData: boolean = false): IRowNode[] | any[] {
    // 1) Contexts
    const rowGroupCtx = getRowGroupContext(rowNode);
    const { pivotFields, pivotValues } = getPivotContext(api, colDef);

    // 2) Candidate leaves are the descendants under the clicked group row
    const candidates = collectLeafDescendants(rowNode);

    // 3) Match predicate: all row-group pairs + as many pivot keys as present on the clicked column
    const matches = (data: any) => {
        for (const [field, val] of Object.entries(rowGroupCtx)) {
            if (data[field] !== val) return false;
        }
        for (let i = 0; i < pivotValues.length; i++) {
            const pf = pivotFields[i];
            if (!pf) break;
            if (data[pf] !== pivotValues[i]) return false;
        }
        return true;
    };

    const hits = candidates.filter((n) => n.data && matches(n.data));
    return returnData ? hits.map((n) => n.data) : hits;
}

/**
 * Convenience overload that takes the AG Grid CellDoubleClickedEvent directly.
 */
export function getDrilldownFromDoubleClick(evt: CellDoubleClickedEvent, returnData: boolean = false): IRowNode[] | any[] {
    return getDrilldownForPivotCell(evt.api, evt.node, evt.column.getColDef(), returnData);
}

/**
 * Checks whether the given response if of validation response or contains an errorcode not like `'0'` | `0`.
 *
 * @param response the response to check if is an error.
 * @returns `true` if the given response is a validation or error, `false` if not.
 */
export function isErrorOrValidation(response: unknown) {
    if (!!response && typeof response === 'object' && 'message' in response && response.message === true) {
        return true;
    } else if (isErrorResponse(response)) {
        return true;
    }
    return false;
}

/**
 * Checks whether the given object is of a select-type error-response.
 *
 * @param response the response to check.
 * @returns `true` if it is a select type error-response, `false` if not.
 */
export function isErrorResponse(response: unknown) {
    return Array.isArray(response) && !!response[0] && typeof response[0] === 'object' && 'errorcode' in response[0] && '' + response[0].errorcode !== '0';
}

/**
 * Returns the key name for the datatask configuration required by the given state.
 *
 * @param state the state to get the keyname of datatask configuration for.
 * @returns the key name for the datatask configuration or `null` if state not supported.
 */
export function getDataTaskId(state: ChangeState): 'addRow' | 'saveData' | 'removeRow' | null {
    let dataTaskId: keyof GridDataTask | null = null;

    switch (state) {
        case 'add':
            dataTaskId = 'addRow';
            break;
        case 'update':
        case 'all':
        case 'read':
            dataTaskId = 'saveData';
            break;
        case 'remove':
            dataTaskId = 'removeRow';
            break;
        default:
            break;
    }

    return dataTaskId;
}
